import React, { useEffect, useRef, useState } from 'react';
import { Wrapper, Status } from '@googlemaps/react-wrapper';
import { Box, Typography, CircularProgress, Alert, Card, CardContent } from '@mui/material';
import { LocationOn as LocationIcon } from '@mui/icons-material';
import { configurationService } from '../../../services/configurationService';

interface CaixaMapComponentProps {
  latitude: number;
  longitude: number;
  caixaName: string;
  address: string;
  height?: string | number;
}

// Componente interno do mapa que usa a API do Google Maps
const MapComponent: React.FC<{
  center: google.maps.LatLngLiteral;
  zoom: number;
  caixaName: string;
  address: string;
}> = ({ center, zoom, caixaName, address }) => {
  const ref = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);

  useEffect(() => {
    if (ref.current && !mapRef.current) {
      // Criar o mapa
      mapRef.current = new google.maps.Map(ref.current, {
        center,
        zoom,
        mapTypeId: google.maps.MapTypeId.ROADMAP,
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels.text',
            stylers: [{ visibility: 'off' }]
          }
        ],
        mapTypeControl: true,
        streetViewControl: true,
        fullscreenControl: true,
        zoomControl: true
      });

      // Criar marcador customizado para CAIXA
      markerRef.current = new google.maps.Marker({
        position: center,
        map: mapRef.current,
        title: caixaName,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 12,
          fillColor: '#1976d2',
          fillOpacity: 0.8,
          strokeColor: '#ffffff',
          strokeWeight: 3,
        },
        animation: google.maps.Animation.DROP
      });

      // InfoWindow com informações da CAIXA
      const infoWindow = new google.maps.InfoWindow({
        content: `
          <div style="padding: 8px; min-width: 200px;">
            <h3 style="margin: 0 0 8px 0; color: #1976d2; font-size: 16px;">
              📍 ${caixaName}
            </h3>
            <p style="margin: 0; color: #666; font-size: 14px;">
              ${address}
            </p>
            <p style="margin: 8px 0 0 0; color: #999; font-size: 12px;">
              ${center.lat.toFixed(6)}, ${center.lng.toFixed(6)}
            </p>
          </div>
        `
      });

      // Abrir InfoWindow ao clicar no marcador
      markerRef.current.addListener('click', () => {
        infoWindow.open(mapRef.current, markerRef.current);
      });

      // Abrir InfoWindow automaticamente após 1 segundo
      setTimeout(() => {
        infoWindow.open(mapRef.current, markerRef.current);
      }, 1000);
    }
  }, [center, zoom, caixaName, address]);

  // Atualizar posição quando coordenadas mudarem
  useEffect(() => {
    if (mapRef.current && markerRef.current) {
      mapRef.current.setCenter(center);
      markerRef.current.setPosition(center);
    }
  }, [center]);

  return (
    <div 
      ref={ref} 
      style={{ 
        width: '100%', 
        height: '100%',
        borderRadius: '8px'
      }} 
    />
  );
};

// Componente de status para loading e erros
const MapStatus: React.FC<{ status: Status }> = ({ status }) => {
  if (status === Status.LOADING) {
    return (
      <Box 
        display="flex" 
        flexDirection="column" 
        alignItems="center" 
        justifyContent="center" 
        height="100%"
        gap={2}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Carregando mapa...
        </Typography>
      </Box>
    );
  }

  if (status === Status.FAILURE) {
    return (
      <Alert severity="error" sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Erro ao carregar mapa
          </Typography>
          <Typography variant="body2">
            Verifique a configuração da API Key do Google Maps
          </Typography>
        </Box>
      </Alert>
    );
  }

  return null;
};

// Componente principal exportado
const CaixaMapComponent: React.FC<CaixaMapComponentProps> = ({
  latitude,
  longitude,
  caixaName,
  address,
  height = 300
}) => {
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const center = { lat: latitude, lng: longitude };
  const zoom = 16;

  // Buscar API Key da configuração
  useEffect(() => {
    const loadApiKey = async () => {
      try {
        setLoading(true);
        setError(null);

        const config = await configurationService.getConfigurationByKey('google_maps_api_key');
        
        if (config && config.valor && config.ativo) {
          setApiKey(config.valor);
        } else {
          throw new Error('API Key do Google Maps não configurada ou inativa');
        }
      } catch (err: any) {
        console.error('Erro ao carregar API Key do Google Maps:', err);
        setError(err.message || 'Erro ao carregar configuração do mapa');
      } finally {
        setLoading(false);
      }
    };

    loadApiKey();
  }, []);

  // Loading inicial da API Key
  if (loading) {
    return (
      <Card sx={{ height }}>
        <CardContent sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Box textAlign="center">
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Carregando configurações do mapa...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Erro ao carregar API Key
  if (error || !apiKey) {
    return (
      <Card sx={{ height }}>
        <CardContent sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Alert severity="warning" sx={{ width: '100%', maxWidth: 400 }}>
            <Typography variant="subtitle2" gutterBottom>
              Mapa não disponível
            </Typography>
            <Typography variant="body2">
              {error || 'API Key do Google Maps não configurada'}
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              Configure a chave 'google_maps_api_key' nas configurações do sistema
            </Typography>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  // Coordenadas não disponíveis
  if (!latitude || !longitude) {
    return (
      <Card sx={{ height }}>
        <CardContent sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Box textAlign="center">
            <LocationIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body1" color="text.secondary">
              Coordenadas não disponíveis
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Localização não pode ser exibida no mapa
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Renderizar mapa com API Key válida
  return (
    <Card sx={{ height, overflow: 'hidden' }}>
      <CardContent sx={{ height: '100%', p: 0 }}>
        <Wrapper 
          apiKey={apiKey}
          render={(status) => <MapStatus status={status} />}
        >
          <MapComponent 
            center={center} 
            zoom={zoom} 
            caixaName={caixaName}
            address={address}
          />
        </Wrapper>
      </CardContent>
    </Card>
  );
};

export default CaixaMapComponent; 